import React, { useContext, useEffect, useState } from "react";

export const ScreenSizeNumericValues = {
	"xs": 0,
	"sm": 1,
	"md": 2,
	"lg": 3,
	"xl": 4,
	"2xl": 5,
	"3xl": 6
} as const;

export type ScreenSizeView = keyof typeof ScreenSizeNumericValues;

export const screenSizeIsAtLeast = (screenSize: ScreenSizeView, compareTo: ScreenSizeView) => {
	return ScreenSizeNumericValues[screenSize] >= ScreenSizeNumericValues[compareTo];
};

export const screenSizeIsNoMoreThan = (screenSize: ScreenSizeView, compareTo: ScreenSizeView) => {
	return ScreenSizeNumericValues[screenSize] <= ScreenSizeNumericValues[compareTo];
};

export const screenSizeIsConsideredMobile = (screenSize: ScreenSizeView) => {
	return screenSizeIsNoMoreThan(screenSize, "sm");
};

export const screenSizeIsConsideredDesktop = (screenSize: ScreenSizeView) => {
	return screenSizeIsAtLeast(screenSize, "lg");
};

export const screenSizeIsConsideredWideScreen = (screenSize: ScreenSizeView) => {
	return screenSizeIsAtLeast(screenSize, "3xl");
};

export interface ScreenSizeProviderProps {
	children: React.ReactNode;
}

export const ScreenSizeProvider = ({ children }: ScreenSizeProviderProps) => {
	const [screenWidth, setScreenWidth] = useState(window.innerWidth);

	useEffect(() => {
		const onResize = () => {
			setScreenWidth(window.innerWidth);
		};

		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	return (
		<ctx.Provider value={determineScreenSizeView(screenWidth)}>
			{children}
		</ctx.Provider>
	);
};

const determineScreenSizeView = (screenWidth: number): ScreenSizeView => {
	if (screenWidth >= 1920) return "3xl";
	if (screenWidth >= 1536) return "2xl";
	if (screenWidth >= 1280) return "xl";
	if (screenWidth >= 1024) return "lg";
	if (screenWidth >= 768) return "md";
	if (screenWidth >= 640) return "sm";

	return "xs";
};

const ctx = React.createContext(determineScreenSizeView(window.innerWidth));

export const useScreenSize = () => useContext(ctx);
